import React, { useEffect, useState } from 'react';
import { Loader2, Plus, X, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { fetchContent, adminUpdateContent } from '../../lib/api';
import { ICON_OPTIONS, DEFAULT_CONTENT } from '../../mock';
import { useToast } from '../../hooks/use-toast';

const Card = ({ title, children }) => (
  <div className="bg-white rounded-md p-6 shadow-sm ring-1 ring-[#e6e4d7]">
    <h2 className="font-display text-xl font-semibold text-[#123020] mb-4">{title}</h2>
    {children}
  </div>
);

const ContentPanel = () => {
  const { toast } = useToast();
  const [c, setC] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent().then((d) => { if (d) setC(d); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const setHero = (k, v) => setC((p) => ({ ...p, hero: { ...p.hero, [k]: v } }));
  const setAbout = (k, v) => setC((p) => ({ ...p, about: { ...p.about, [k]: v } }));
  const setContact = (k, v) => setC((p) => ({ ...p, contact: { ...p.contact, [k]: v } }));

  const setPoint = (i, v) => setC((p) => ({ ...p, about: { ...p.about, points: p.about.points.map((x, idx) => (idx === i ? v : x)) } }));
  const addPoint = () => setC((p) => ({ ...p, about: { ...p.about, points: [...p.about.points, ''] } }));
  const removePoint = (i) => setC((p) => ({ ...p, about: { ...p.about, points: p.about.points.filter((_, idx) => idx !== i) } }));

  const setService = (i, k, v) => setC((p) => ({ ...p, services: p.services.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const addService = () => setC((p) => ({ ...p, services: [...p.services, { icon: 'leaf', title: '', desc: '' }] }));
  const removeService = (i) => setC((p) => ({ ...p, services: p.services.filter((_, idx) => idx !== i) }));

  const setArea = (i, v) => setC((p) => ({ ...p, contact: { ...p.contact, areas: p.contact.areas.map((x, idx) => (idx === i ? v : x)) } }));
  const addArea = () => setC((p) => ({ ...p, contact: { ...p.contact, areas: [...p.contact.areas, ''] } }));
  const removeArea = (i) => setC((p) => ({ ...p, contact: { ...p.contact, areas: p.contact.areas.filter((_, idx) => idx !== i) } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { hero: c.hero, about: { ...c.about, points: c.about.points.filter((x) => x.trim()) }, services: c.services.filter((s) => s.title.trim()), contact: { ...c.contact, areas: c.contact.areas.filter((x) => x.trim()) } };
      await adminUpdateContent(payload);
      toast({ title: 'Website content saved', description: 'Your changes are now live on the site.' });
    } catch (e) { toast({ title: 'Save failed', variant: 'destructive' }); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-24 text-[#5a6b60]"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 sticky top-16 lg:top-0 z-10">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-[#123020]">Website Content</h1>
          <p className="text-[#5a6b60] mt-1 text-[14px]">Edit text shown across your public site.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#86c93c] hover:bg-[#75b52f] text-[#123020] font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}</Button>
      </div>

      <div className="space-y-6">
        <Card title="Hero Section">
          <div className="space-y-4">
            <div><Label className="font-medium">Headline – Line 1</Label><Input value={c.hero.line1} onChange={(e) => setHero('line1', e.target.value)} className="mt-1.5" /></div>
            <div><Label className="font-medium">Headline – Line 2 (highlighted)</Label><Input value={c.hero.line2} onChange={(e) => setHero('line2', e.target.value)} className="mt-1.5" /></div>
            <div><Label className="font-medium">Description</Label><Textarea value={c.hero.description} onChange={(e) => setHero('description', e.target.value)} rows={3} className="mt-1.5 resize-none" /></div>
          </div>
        </Card>

        <Card title="About Us">
          <div className="space-y-4">
            <div><Label className="font-medium">Heading</Label><Input value={c.about.heading} onChange={(e) => setAbout('heading', e.target.value)} className="mt-1.5" /></div>
            <div><Label className="font-medium">Paragraph</Label><Textarea value={c.about.paragraph} onChange={(e) => setAbout('paragraph', e.target.value)} rows={4} className="mt-1.5 resize-none" /></div>
            <div>
              <Label className="font-medium">Highlights</Label>
              <div className="mt-1.5 space-y-2">
                {c.about.points.map((pt, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={pt} onChange={(e) => setPoint(i, e.target.value)} />
                    <button onClick={() => removePoint(i)} className="h-10 w-10 flex items-center justify-center rounded text-red-500 hover:bg-red-50 shrink-0"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                <Button variant="outline" onClick={addPoint} className="border-dashed"><Plus className="mr-2 h-4 w-4" /> Add highlight</Button>
              </div>
            </div>
          </div>
        </Card>

        <Card title="Services">
          <div className="space-y-3">
            {c.services.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-4 sm:col-span-3">
                  <Select value={s.icon} onValueChange={(v) => setService(i, 'icon', v)}>
                    <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>{ICON_OPTIONS.map((ic) => (<SelectItem key={ic} value={ic}>{ic}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <Input className="col-span-8 sm:col-span-4" value={s.title} onChange={(e) => setService(i, 'title', e.target.value)} placeholder="Service title" />
                <Input className="col-span-11 sm:col-span-4" value={s.desc} onChange={(e) => setService(i, 'desc', e.target.value)} placeholder="Short description" />
                <button onClick={() => removeService(i)} className="col-span-1 h-10 w-10 flex items-center justify-center rounded text-red-500 hover:bg-red-50"><X className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="outline" onClick={addService} className="border-dashed"><Plus className="mr-2 h-4 w-4" /> Add service</Button>
          </div>
        </Card>

        <Card title="Contact Details">
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="font-medium">Phone</Label><Input value={c.contact.phone} onChange={(e) => setContact('phone', e.target.value)} className="mt-1.5" /></div>
              <div><Label className="font-medium">Email</Label><Input value={c.contact.email} onChange={(e) => setContact('email', e.target.value)} className="mt-1.5" /></div>
            </div>
            <div>
              <Label className="font-medium">Service Areas</Label>
              <div className="mt-1.5 space-y-2">
                {c.contact.areas.map((a, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={a} onChange={(e) => setArea(i, e.target.value)} />
                    <button onClick={() => removeArea(i)} className="h-10 w-10 flex items-center justify-center rounded text-red-500 hover:bg-red-50 shrink-0"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                <Button variant="outline" onClick={addArea} className="border-dashed"><Plus className="mr-2 h-4 w-4" /> Add area</Button>
              </div>
            </div>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[#86c93c] hover:bg-[#75b52f] text-[#123020] font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}</Button>
        </div>
      </div>
    </div>
  );
};

export default ContentPanel;
